import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, loading, format_date, Select, Pagination, DateInput } from "../components/CustomComponent";

const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  borderRadius: 24,
  boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)",
};

const summaryCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
};

const PAGE_SIZE = 20;

// action ที่ middleware บันทึกไว้ (create / update / delete)
const ACTION_OPTIONS = [
  { value: "create", label: "สร้าง (create)" },
  { value: "update", label: "แก้ไข (update)" },
  { value: "delete", label: "ลบ (delete)" },
];

const ACTION_STYLE = {
  create: { background: "rgba(47, 181, 114, 0.14)", color: "#188251", label: "สร้าง" },
  update: { background: "rgba(12, 117, 230, 0.14)", color: "#0b4f84", label: "แก้ไข" },
  delete: { background: "rgba(229, 83, 83, 0.14)", color: "#b63636", label: "ลบ" },
};

// ชื่อโมดูลภาษาไทย เทียบจาก path ที่ middleware เก็บไว้ใน entity_type
const ENTITY_LABELS = {
  users: "บัญชีผู้ใช้",
  roles: "บทบาท/สิทธิ์",
  employees: "พนักงาน",
  payrolls: "เงินเดือน",
  companies: "ข้อมูลองค์กร",
  "finance-categories": "หมวดหมู่การเงิน",
  "finance-transactions": "ธุรกรรมการเงิน",
  "employee-finance": "การเงินพนักงาน",
  "project-timelines": "ไทม์ไลน์โปรเจค",
  "project-tasks": "งานในโปรเจค",
  "company-documents": "เอกสารการเงิน",
  documents: "ไฟล์เอกสาร",
  attendance: "เวลาเข้าออกงาน",
  leaves: "การลา",
  holidays: "วันหยุดบริษัท",
  partners: "ลูกค้า/คู่ค้า",
};

const entityLabel = (type) => ENTITY_LABELS[type] || type || "-";

const getActionStyle = (action) => ACTION_STYLE[action] || { background: "rgba(108, 122, 137, 0.14)", color: "#566575", label: action || "-" };

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_action: {}, by_entity: {} });
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [detail, setDetail] = useState(null);

  // ค่าที่พิมพ์อยู่ในช่องกรอง กับค่าที่ยิงไปจริง แยกกันเพื่อให้กดค้นหาแล้วค่อยยิง
  const [searchInput, setSearchInput] = useState("");
  const [actionInput, setActionInput] = useState("");
  const [entityInput, setEntityInput] = useState("");
  const [dateFromInput, setDateFromInput] = useState("");
  const [dateToInput, setDateToInput] = useState("");
  const [filters, setFilters] = useState({ data_search: "", action: "", entity_type: "", start_date: "", end_date: "" });

  const getLogs = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/audit-logs", {
      page,
      page_size: PAGE_SIZE,
      ...(filters.data_search ? { data_search: filters.data_search } : {}),
      ...(filters.action ? { action: filters.action } : {}),
      ...(filters.entity_type ? { entity_type: filters.entity_type } : {}),
      ...(filters.start_date ? { start_date: `${filters.start_date} 00:00:00` } : {}),
      ...(filters.end_date ? { end_date: `${filters.end_date} 23:59:59` } : {}),
    });

    if (result?.status) {
      setLogs(result.data || []);
      const count = result.pagination?.count || 0;
      setPageCount(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    }
    loading(false);
  }, [page, filters]);

  const getStats = useCallback(async () => {
    const result = await GET("v1/admin/audit-logs/stats");
    if (result?.status && result.data) setStats(result.data);
  }, []);

  useEffect(() => {
    getLogs();
  }, [getLogs]);

  useEffect(() => {
    getStats();
  }, [getStats]);

  const handleSearch = () => {
    setPage(1);
    setFilters({
      data_search: searchInput.trim(),
      action: actionInput,
      entity_type: entityInput,
      start_date: dateFromInput,
      end_date: dateToInput,
    });
  };

  const handleReset = () => {
    setSearchInput("");
    setActionInput("");
    setEntityInput("");
    setDateFromInput("");
    setDateToInput("");
    setPage(1);
    setFilters({ data_search: "", action: "", entity_type: "", start_date: "", end_date: "" });
  };

  // ตัวเลือกโมดูลสร้างจากสถิติจริง จะได้ไม่โชว์โมดูลที่ยังไม่เคยมีรายการ
  const entityOptions = useMemo(
    () =>
      Object.keys(stats.by_entity || {})
        .filter(Boolean)
        .sort()
        .map((key) => ({ value: key, label: entityLabel(key) })),
    [stats]
  );

  const summaryCards = [
    { label: "รายการทั้งหมด", value: stats.total || 0, color: "#0c76e5", icon: "fa-clipboard-list" },
    { label: "สร้าง", value: stats.by_action?.create || 0, color: "#188251", icon: "fa-plus" },
    { label: "แก้ไข", value: stats.by_action?.update || 0, color: "#0b4f84", icon: "fa-pen" },
    { label: "ลบ", value: stats.by_action?.delete || 0, color: "#b63636", icon: "fa-trash" },
  ];

  return (
    <AppShell title="ประวัติการใช้งานระบบ" description="ตรวจสอบย้อนหลังว่าใครทำอะไรกับข้อมูลในระบบ เมื่อไหร่ และจากเครื่องไหน">
      <div className="row g-3 mb-4">
        {summaryCards.map((card) => (
          <div className="col-6 col-xl-3" key={card.label}>
            <div className="h-100 rounded-4 p-4 d-flex align-items-center gap-3" style={summaryCardStyle}>
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 44, height: 44, borderRadius: 12, background: `${card.color}1f`, color: card.color, fontSize: 18 }}
              >
                <i className={`fa-solid ${card.icon}`} />
              </div>
              <div>
                <p className="small text-uppercase text-secondary mb-1">{card.label}</p>
                <h3 className="h4 mb-0 fw-bold" style={{ color: card.color }}>{card.value.toLocaleString("en-US")}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack mb-4">
        <input
          type="text"
          className="form-control"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && handleSearch()}
          placeholder="ค้นหาเส้นทางหรือ action..."
          style={{ width: "220px", borderRadius: "12px" }}
        />
        <div style={{ width: "170px" }}>
          <Select className="form-control" value={actionInput} options={ACTION_OPTIONS} placeholder="ทุก action" onChange={(value) => setActionInput(value)} />
        </div>
        <div style={{ width: "190px" }}>
          <Select className="form-control" value={entityInput} options={entityOptions} placeholder="ทุกโมดูล" onChange={(value) => setEntityInput(value)} />
        </div>
        <DateInput className="form-control" value={dateFromInput} onChange={(event) => setDateFromInput(event.target.value)} title="ตั้งแต่วันที่" style={{ width: "160px", borderRadius: "12px" }} />
        <span className="text-secondary">ถึง</span>
        <DateInput className="form-control" value={dateToInput} onChange={(event) => setDateToInput(event.target.value)} title="ถึงวันที่" style={{ width: "160px", borderRadius: "12px" }} />
        <button type="button" className="btn btn-primary px-4" onClick={handleSearch} style={{ borderRadius: "12px" }}>ค้นหา</button>
        <button
          type="button"
          className="btn btn-light px-4"
          onClick={handleReset}
          style={{ borderRadius: "12px", border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
        >
          ล้างค่า
        </button>
      </div>

      <div className="p-3 p-md-4" style={sectionCardStyle}>
        <h5 className="mb-3 fw-semibold text-dark">รายการล่าสุด</h5>
        <div className="table-responsive table-mobile-cards">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium text-nowrap">เวลา</th>
                <th className="text-secondary fw-medium">ผู้ใช้</th>
                <th className="text-secondary fw-medium text-nowrap">การกระทำ</th>
                <th className="text-secondary fw-medium">โมดูล</th>
                <th className="text-secondary fw-medium">รายละเอียด</th>
                <th className="text-secondary fw-medium text-center">ข้อมูล</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map((item) => {
                  const actionStyle = getActionStyle(item.action);
                  return (
                    <tr key={item.id}>
                      <td data-label="เวลา" className="text-nowrap">{format_date(item.created_at, "dd/mm/yyyy hh:mm")}</td>
                      <td data-label="ผู้ใช้">
                        <div>
                          <div className="fw-medium">{item.user?.full_name || "ระบบ"}</div>
                          <div className="small text-secondary">{item.user?.email || "-"}</div>
                        </div>
                      </td>
                      <td data-label="การกระทำ" className="text-nowrap">
                        <span
                          className="rounded-pill px-3 py-1 small fw-semibold text-nowrap d-inline-block"
                          style={{ background: actionStyle.background, color: actionStyle.color }}
                        >
                          {actionStyle.label}
                        </span>
                      </td>
                      <td data-label="โมดูล">{entityLabel(item.entity_type)}</td>
                      <td data-label="รายละเอียด">
                        <div className="small text-secondary">{item.entity_name || "-"}</div>
                        <div className="small text-secondary">{item.ip_address || "-"}</div>
                      </td>
                      <td data-label="ข้อมูล" className="text-center">
                        <button className="btn btn-sm btn-outline-secondary rounded-3" title="ดูข้อมูลที่ส่ง" onClick={() => setDetail(item)}>
                          <i className="fa-solid fa-eye" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-secondary py-5">ไม่พบประวัติการใช้งาน</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination count={pageCount} page={page} onChange={(event, value) => setPage(value)} color="primary" shape="rounded" />
          </div>
        )}
      </div>

      <Modal show={!!detail} onHide={() => setDetail(null)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h5" className="fw-semibold">รายละเอียดการเปลี่ยนแปลง</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <>
              <div className="row g-3 mb-3">
                {[
                  { label: "เวลา", value: format_date(detail.created_at, "dd/mm/yyyy hh:mm") },
                  { label: "ผู้ใช้", value: `${detail.user?.full_name || "ระบบ"} (${detail.user?.email || "-"})` },
                  { label: "การกระทำ", value: `${getActionStyle(detail.action).label} · ${detail.metadata?.method || "-"}` },
                  { label: "โมดูล", value: entityLabel(detail.entity_type) },
                  { label: "เส้นทาง", value: detail.entity_name || "-" },
                  { label: "IP", value: detail.ip_address || "-" },
                ].map((row) => (
                  <div className="col-md-6" key={row.label}>
                    <div className="small text-secondary">{row.label}</div>
                    <div className="fw-semibold" style={{ wordBreak: "break-word" }}>{row.value}</div>
                  </div>
                ))}
              </div>

              <div className="small text-secondary mb-1">ข้อมูลที่ส่งมา</div>
              <pre
                className="p-3 rounded-3 mb-0"
                style={{ background: "#f6f9fc", border: "1px solid #e6edf5", fontSize: 12, maxHeight: 320, overflow: "auto" }}
              >
                {detail.new_values ? JSON.stringify(detail.new_values, null, 2) : "ไม่มีข้อมูล (เช่น คำสั่งลบ)"}
              </pre>

              <div className="small text-secondary mt-3" style={{ wordBreak: "break-word" }}>
                {detail.user_agent || ""}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setDetail(null)}>ปิด</button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
}
